import { isNil, omit, pick } from 'lodash';
import type Collectivite from '../interfaces/collectivite';
import type {
	UtilisateurCollectivite,
	UtilisateurCollectiviteResponse,
} from '../interfaces/utilisateurCollectivite';

interface StructureJobResponse {
	version: string;
	data: UtilisateurCollectiviteResponse[];
}

export interface ParsedStructureData {
	version: string;
	utilisateurs: UtilisateurCollectivite[];
	collectivites: Collectivite[];
}

const buildCollectiviteEntry = (
	entite: UtilisateurCollectiviteResponse,
	utilisateur: UtilisateurCollectiviteResponse['utilisateurs'][number],
) => ({
	id_e: entite?.id_e,
	siren: entite?.siren,
	libelle: entite?.denomination,
	codeClientSF: entite?.codeClientSF,
	isEntiteCommercialeDuClientConnecte:
		entite.isEntiteCommercialeDuClientConnecte,
	roles: utilisateur.roles,
	bureaux: utilisateur?.bureaux,
	...pick(utilisateur, [
		'bureaux',
		'roles',
		'slow',
		'loginSlow',
		'roleSlow',
		'collectiviteSlowId',
		'collectiviteSlowName',
	]),
	certificat: {
		dateFinValidite: utilisateur?.certificat?.dateFinValidite
			? new Date(utilisateur.certificat.dateFinValidite)
			: new Date(0),
	},
	...(utilisateur?.identifiantUtilisateurSlow
		? {
				identifiantUtilisateurSlow: Number(
					utilisateur.identifiantUtilisateurSlow,
				),
			}
		: {}),
});

const parseStructureJob = (resultat: string): ParsedStructureData => {
	const { version, data: response }: StructureJobResponse =
		JSON.parse(resultat);

	const utilisateursData: UtilisateurCollectivite[] = [];
	const collectivitesData: Collectivite[] = [];

	response?.forEach((entite) => {
		collectivitesData.push({
			id_e: entite.id_e,
			siren: entite.siren,
			libelle: entite.denomination,
			codeClientSF: entite.codeClientSF,
		});

		entite?.utilisateurs?.forEach((utilisateur) => {
			if (utilisateur?.collectiviteSlowId) {
				const collectiviteIndex = collectivitesData.findIndex(
					(c) => c.id_e === entite.id_e,
				);
				if (collectiviteIndex > -1) {
					collectivitesData[collectiviteIndex] = {
						...collectivitesData[collectiviteIndex],
						collectiviteSlowId: utilisateur.collectiviteSlowId.toString(),
						collectiviteSlowName: utilisateur.collectiviteSlowName,
					};
				}
			}

			const existingUtilisateurIndex = utilisateursData?.findIndex(
				(u) => u.login === utilisateur.login,
			);
			if (!isNil(utilisateur?.login) && existingUtilisateurIndex > -1) {
				// Enrichir les propriétés user-level manquantes (cas utilisateur secondaire vu avant le principal)
				const userLevelKeys = [
					'identifiantParapheur',
					'identifiantPastell',
					'identifiantUtilisateurSlow',
				] as const;
				userLevelKeys.forEach((key) => {
					if (
						utilisateur[key] &&
						!utilisateursData[existingUtilisateurIndex][key]
					) {
						(
							utilisateursData[existingUtilisateurIndex] as Record<
								string,
								unknown
							>
						)[key] = utilisateur[key];
					}
				});
				utilisateursData[existingUtilisateurIndex].collectivites.push(
					buildCollectiviteEntry(entite, utilisateur),
				);
			} else {
				utilisateursData.push({
					...omit(utilisateur, [
						'bureaux',
						'roles',
						'slow',
						'loginSlow',
						'certificat',
						'roleSlow',
						'collectiviteSlowId',
						'collectiviteSlowName',
					]),
					collectivites: [buildCollectiviteEntry(entite, utilisateur)],
					isUpdated: false,
				} as UtilisateurCollectivite);
			}
		});
	});

	return {
		version,
		utilisateurs: utilisateursData,
		collectivites: collectivitesData,
	};
};

export default parseStructureJob;
